/** The available search type options in the unified station search dropdown.
 * Requirements: 8.1 */
export type StationSearchType = 'stationCode' | 'deviceId' | 'wmo' | 'icao' | 'iata' | 'mgID' | 'mmi' | 'allTags';

/** Maps StationSearchType to the API tag key parameter.
 * Requirements: 8.1 */
export const SEARCH_TYPE_TO_TAG_KEY: Record<StationSearchType, string> = {
  stationCode: '',
  // A device's Station ID is not a backend-searchable station tag — it lives in
  // each station's devices[]. The search store matches it client-side against the
  // loaded station list, so this tag key is unused (kept for the Record's totality).
  deviceId: '',
  wmo: 'wmo',
  icao: 'icao',
  iata: 'iata',
  mgID: 'mgID',
  mmi: 'mmi',
  allTags: '',
};

/** Dropdown display configuration for the unified station search bar.
 * Requirements: 1.1 */
export const SEARCH_TYPE_OPTIONS: Array<{ value: StationSearchType; label: string }> = [
  { value: 'stationCode', label: 'Station Code' },
  { value: 'deviceId', label: 'Device ID' },
  { value: 'wmo', label: 'WMO' },
  { value: 'icao', label: 'ICAO' },
  { value: 'iata', label: 'IATA' },
  { value: 'mgID', label: 'MGID' },
  { value: 'mmi', label: 'MMI' },
  { value: 'allTags', label: 'All Tags' },
];

/** Parses raw input text into deduplicated, trimmed, non-empty search values.
 * Splits on commas, spaces, and tabs.
 * Requirements: 2.2, 2.6 */
export function parseSearchValues(rawInput: string): string[] {
  const values = rawInput
    .split(/[,\s\t]+/)
    .map(v => v.trim())
    .filter(v => v.length > 0);
  const unique = [...new Set(values)];
  return unique;
}

/** Returns the API tag key for a given StationSearchType.
 * Requirements: 8.2, 8.3, 8.4 */
export function mapSearchTypeToTagKey(searchType: StationSearchType): string {
  return SEARCH_TYPE_TO_TAG_KEY[searchType];
}

/**
 * Reverse of SEARCH_TYPE_TO_TAG_KEY: API tag key → the search type it came from.
 *
 * Returns null for an unknown key AND for the empty one — stationCode, deviceId and
 * allTags all map to '', so an empty key identifies no single type and must not be
 * cast to one (that would mislabel a search as "Station"). Callers that hold only a
 * tag key use this to recover the human label; a null means "no type prefix".
 */
export function tagKeyToSearchType(tagKey: string): StationSearchType | null {
  if (!tagKey) return null;
  const hit = (Object.keys(SEARCH_TYPE_TO_TAG_KEY) as StationSearchType[])
    .find(t => SEARCH_TYPE_TO_TAG_KEY[t] === tagKey);
  return hit ?? null;
}

/** Display label for a search type, e.g. 'mgID' → 'MGID'. */
export function searchTypeLabel(searchType: StationSearchType): string {
  return SEARCH_TYPE_OPTIONS.find(o => o.value === searchType)?.label ?? 'Station';
}

/**
 * Human phrase naming what a station search asked for — quoted back by the empty
 * states so "No station matches MGID 1506235" reads as an answer to the question
 * the user actually asked, rather than a generic "no results".
 *
 * One value  → `MGID 1506235`
 * Two values → `MGID 1506235 and 1506236`
 * More       → `MGID 1506235 and 4 others`
 * All Tags   → drops the type prefix (the values aren't of one named kind).
 */
export function describeSearchValues(searchType: StationSearchType, values: string[]): string {
  const prefix = searchType === 'allTags' ? '' : `${searchTypeLabel(searchType)} `;
  if (values.length === 0) return prefix.trim() || 'your search';
  if (values.length === 1) return `${prefix}${values[0]}`;
  if (values.length === 2) return `${prefix}${values[0]} and ${values[1]}`;
  return `${prefix}${values[0]} and ${values.length - 1} others`;
}
